import {AppDispatch} from 'src/redux/store'
import {handleAppAfterInstallationAsync} from 'src/redux/actions/authActions'
import {
  tiktokPlatformData,
  tiktokPlatformFinalizeRequestKey,
  tiktokPlatformFinalizeResponseKey,
  walmartPlatformData,
  walmartPlatformFinalizeRequestKey,
  walmartPlatformFinalizeResponseKey,
} from './constants'

type FinalizeResult = {
  timestamp: string
  status: 'fulfilled' | 'rejected'
  response?: any
  error?: string
}

type InstallablePlatformName = 'TIKTOK' | 'WALMART'

const platformFinalizeConfig: Record<
  InstallablePlatformName,
  {
    storageKey: string
    requestKey: string
    responseKey: string
  }
> = {
  TIKTOK: {
    storageKey: tiktokPlatformData,
    requestKey: tiktokPlatformFinalizeRequestKey,
    responseKey: tiktokPlatformFinalizeResponseKey,
  },
  WALMART: {
    storageKey: walmartPlatformData,
    requestKey: walmartPlatformFinalizeRequestKey,
    responseKey: walmartPlatformFinalizeResponseKey,
  },
}

let finalizingPlatformName: InstallablePlatformName | null = null

const isSuccessfulFinalizeResponse = (response: any) => {
  const statusCode = response?.status_code ?? response?.data?.status_code ?? response?.status
  if (statusCode === undefined || statusCode === null) {
    return true
  }

  const numericStatusCode = Number(statusCode)
  return numericStatusCode >= 200 && numericStatusCode < 300
}

const getFinalizeResponseMessage = (response: any) =>
  response?.message ||
  response?.massage ||
  response?.data?.message ||
  response?.data?.massage ||
  'handle-app-after-installation failed'

const getAuthenticatedUserId = (authenticatedUser: any) =>
  authenticatedUser?.id ||
  authenticatedUser?.user_id ||
  authenticatedUser?.userid ||
  authenticatedUser?.data?.id ||
  authenticatedUser?.data?.user_id ||
  authenticatedUser?.data?.userid ||
  authenticatedUser?.data?.user?.id ||
  authenticatedUser?.user?.id ||
  null

const getAuthenticatedUserEmail = (authenticatedUser: any) =>
  authenticatedUser?.email ||
  authenticatedUser?.data?.email ||
  authenticatedUser?.user?.email ||
  authenticatedUser?.data?.user?.email ||
  ''

const getAuthenticatedUsername = (authenticatedUser: any) =>
  authenticatedUser?.userAdditionalInfo?.username ||
  authenticatedUser?.data?.userAdditionalInfo?.username ||
  authenticatedUser?.name ||
  authenticatedUser?.data?.name ||
  authenticatedUser?.username ||
  authenticatedUser?.data?.username ||
  ''

export const hasPendingPlatformInstallation = () =>
  Object.values(platformFinalizeConfig).some(({storageKey}) => Boolean(localStorage.getItem(storageKey)))

export const getPendingPlatformInstallationName = () =>
  (Object.keys(platformFinalizeConfig) as InstallablePlatformName[]).find(
    (platformName) => Boolean(localStorage.getItem(platformFinalizeConfig[platformName].storageKey))
  ) || null

export const clearPendingPlatformInstallation = (platformName: string) => {
  const normalizedPlatformName = platformName.toUpperCase() as InstallablePlatformName
  const storageKey = platformFinalizeConfig[normalizedPlatformName]?.storageKey
  if (storageKey) {
    localStorage.removeItem(storageKey)
  }
}

export const getPlatformInstallationStorageKey = (platformName: string) => {
  const normalizedPlatformName = platformName.toUpperCase() as InstallablePlatformName
  return platformFinalizeConfig[normalizedPlatformName]?.storageKey || tiktokPlatformData
}

export const getSupportedInstallablePlatform = (platformName: string | null | undefined) => {
  const normalizedPlatformName = (platformName || '').toUpperCase()
  return normalizedPlatformName === 'TIKTOK' || normalizedPlatformName === 'WALMART'
    ? (normalizedPlatformName as InstallablePlatformName)
    : null
}

export const finalizeTikTokInstallationIfNeeded = async (
  dispatch: AppDispatch,
  authenticatedUser: any
) => {
  if (finalizingPlatformName) {
    return false
  }

  const platformToFinalize = getPendingPlatformInstallationName()

  if (!platformToFinalize) {
    return false
  }

  const config = platformFinalizeConfig[platformToFinalize]
  const payloadRaw = localStorage.getItem(config.storageKey)
  if (!payloadRaw) {
    return false
  }

  let parsedPayload: any = null
  try {
    parsedPayload = JSON.parse(payloadRaw || '{}')
  } catch (error) {
    localStorage.removeItem(config.storageKey)
    return false
  }

  const normalizedPlatformName = (parsedPayload?.platform_name || platformToFinalize).toUpperCase()
  if (normalizedPlatformName !== platformToFinalize) {
    return false
  }

  finalizingPlatformName = platformToFinalize

  const resolvedPlatformData =
    typeof parsedPayload?.platform_data === 'string'
      ? parsedPayload.platform_data
      : parsedPayload?.platform_data
      ? JSON.stringify(parsedPayload.platform_data)
      : null
  const authenticatedUserId = getAuthenticatedUserId(authenticatedUser)

  const requestBody = {
    platform_name: parsedPayload?.platform_name || platformToFinalize,
    platform_data: resolvedPlatformData,
    available_platform_name: parsedPayload?.available_platform_name,
    platform_id: parsedPayload?.platform_id,
    user_id: parsedPayload?.user_id || authenticatedUserId,
    country_domain: parsedPayload?.country_domain,
    marketplace: parsedPayload?.marketplace,
    proxies: parsedPayload?.proxies,
    identifier: parsedPayload?.identifier,
    username:
      parsedPayload?.username ||
      getAuthenticatedUsername(authenticatedUser),
    email: parsedPayload?.email || getAuthenticatedUserEmail(authenticatedUser),
    userid: authenticatedUserId,
  }

  if (!requestBody.platform_data || !requestBody.userid) {
    try {
      localStorage.setItem(
        config.responseKey,
        JSON.stringify({
          timestamp: new Date().toISOString(),
          status: 'rejected',
          error: !requestBody.platform_data
            ? 'Missing platform_data before handle-app-after-installation'
            : 'Missing authenticated user id before handle-app-after-installation',
          payload: requestBody,
        })
      )
    } catch (error) {
    }
    finalizingPlatformName = null
    return false
  }

  try {
    localStorage.setItem(
      config.requestKey,
      JSON.stringify({
        timestamp: new Date().toISOString(),
        payload: requestBody,
      })
    )
  } catch (error) {
  }

  let finalizeCallResult: FinalizeResult | null = null

  try {
    const handleAppResponse = await dispatch(
      handleAppAfterInstallationAsync(requestBody)
    ).unwrap()

    if (!isSuccessfulFinalizeResponse(handleAppResponse)) {
      throw new Error(getFinalizeResponseMessage(handleAppResponse))
    }

    finalizeCallResult = {
      timestamp: new Date().toISOString(),
      status: 'fulfilled',
      response: handleAppResponse,
    }
  } catch (error: any) {
    finalizeCallResult = {
      timestamp: new Date().toISOString(),
      status: 'rejected',
      error: error?.message || 'handle-app-after-installation failed',
    }
    throw error
  } finally {
    if (finalizeCallResult) {
      try {
        localStorage.setItem(
          config.responseKey,
          JSON.stringify(finalizeCallResult)
        )
      } catch (error) {
      }
    }
    if (finalizeCallResult?.status === 'fulfilled') {
      localStorage.removeItem(config.storageKey)
    }
    finalizingPlatformName = null
  }

  return true
}
